import { useEffect, FormEventHandler } from 'react';
import { GuestLayout } from '@/Layouts/GuestLayout';
import { InputError } from '@/Components/InputError';
import { InputLabel } from '@/Components/InputLabel';
import { PrimaryButton } from '@/Components/PrimaryButton';
import TextInput from '@/Components/TextInput';
import { Link, useForm } from '@inertiajs/react';
import { Notice } from '@/types';
import { Head } from '@/Components/Head';
import { PREV_ROUTE_STORAGE_KEY } from '@/types/storage.type';

export default function Login({
    status,
    canResetPassword,
    notice,
}: {
    status?: string;
    canResetPassword: boolean;
    notice: Notice;
}) {
    const { data, setData, post, processing, errors, reset } = useForm({
        email: '',
        password: '',
        remember: false,
    });

    useEffect(() => {
        try {
            // NOTE: There is a logic on the Home screen that redirects to the Flows page if there are no flows.
            sessionStorage.setItem(
                PREV_ROUTE_STORAGE_KEY,
                window.location.href,
            );
        } catch (ex) {}

        return () => {
            reset('password');
        };
    }, []);

    const submit: FormEventHandler = (event) => {
        event.preventDefault();

        post(route('login'));
    };

    return (
        <GuestLayout notice={notice}>
            <Head
                title={
                    'ReelFlow Login &ndash; Engage your website visitors through interactive video'
                }
            />

            <img src="/img/logo-motif.png" className="w-[100px] my-4" />

            <h1 className="text-heading-one text-brand-blue font-semibold font-area-normal">
                Sign in to ReelFlow
            </h1>

            {status && (
                <div className="mb-4 font-medium text-sm text-green-600">
                    {status}
                </div>
            )}

            <form onSubmit={submit} className={'grid gap-4'}>
                <div>
                    <InputLabel htmlFor="email" value="Work Email" />

                    <TextInput
                        autoFocus
                        id="email"
                        type="email"
                        name="email"
                        value={data.email}
                        className="mt-1 block w-full"
                        autoComplete="username"
                        onChange={(event) =>
                            setData('email', event.target.value)
                        }
                    />

                    <InputError message={errors.email} className="mt-2" />
                </div>

                <div>
                    <InputLabel htmlFor="password" value="Password" />

                    <TextInput
                        id="password"
                        type="password"
                        name="password"
                        value={data.password}
                        className="mt-1 block w-full"
                        autoComplete="current-password"
                        onChange={(e) => setData('password', e.target.value)}
                    />

                    <InputError message={errors.password} className="mt-2" />
                </div>

                <div>
                    <PrimaryButton disabled={processing}>Sign in</PrimaryButton>
                </div>

                <div>
                    {canResetPassword && (
                        <Link
                            href={route('password.request')}
                            className="underline text-brand-blue rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-blue">
                            Forgotten password?
                        </Link>
                    )}
                </div>

                <div className="text-sm text-brand-blue mt-4">
                    {`By signing-in, you agree to ReelFlow's `}
                    <a
                        href="https://reelflow.com/legal/terms-of-use"
                        target="_blank"
                        className="underline">
                        terms
                    </a>
                    {` and `}
                    <a
                        href="https://reelflow.com/legal/privacy"
                        target="_blank"
                        className="underline">
                        privacy agreements
                    </a>
                </div>
            </form>
        </GuestLayout>
    );
}
